import type { EnvMode, UploadResult } from '@be-link/cos';
import { beLinkCOS } from '@be-link/cos';
import type { UploadFile, UploadProps } from 'antd';
import { message } from 'antd';
import { useEffect, useState } from 'react';

// 项目类型
export type ProjectType = 'jiulv' | 'miniprogram' | 'web' | 'h5' | 'custom';

// 项目选项及其基础路径
export const PROJECT_OPTIONS: {
	label: string;
	value: ProjectType;
	basePath: string;
}[] = [
	{ label: '默认', value: 'custom', basePath: '/beLinkAllSource/static/' },
	{ label: '酒旅项目', value: 'jiulv', basePath: '/beLinkAllSource/static/' },
	{
		label: '电商小程序',
		value: 'miniprogram',
		basePath: '/pandora/miniprogram/',
	},
	{ label: '电商后台', value: 'web', basePath: '/pandora/web/' },
	{ label: '电商H5', value: 'h5', basePath: '/pandora/h5/' },
];

// 环境选项
export const ENV_OPTIONS: { label: string; value: EnvMode }[] = [
	{ label: '测试环境', value: 'test' },
	{ label: '生产环境', value: 'production' },
];

// 已上传文件信息
export interface UploadedFileInfo {
	uid: string;
	name: string;
	url: string;
	isImage: boolean;
	// 仅图片才会有值；从本地 File 读取天然尺寸，避免跨域图片探测问题
	width?: number;
	height?: number;
}

// 检查文件是否为图片
function isImageFile(file: File | UploadFile): boolean {
	const imageTypes = [
		'image/jpeg',
		'image/png',
		'image/gif',
		'image/webp',
		'image/bmp',
		'image/svg+xml',
	];
	const type = 'originFileObj' in file ? file.originFileObj?.type : file.type;
	return imageTypes.includes(type || '');
}

async function getImageDimensionsFromFile(
	file: File,
): Promise<{ width: number; height: number } | null> {
	// 用本地 file 计算天然尺寸：不依赖最终 url 的跨域策略
	const objectUrl = URL.createObjectURL(file);
	try {
		const img = new Image();
		img.decoding = 'async';
		await new Promise<void>((resolve, reject) => {
			img.onload = () => resolve();
			img.onerror = () => reject(new Error('image load failed'));
			img.src = objectUrl;
		});
		// 部分 svg 可能没有内置像素尺寸，自然宽高可能为 0
		const width = img.naturalWidth;
		const height = img.naturalHeight;
		if (!width || !height) return null;
		return { width, height };
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

export function useFileUpload() {
	const [env, setEnv] = useState<EnvMode>('test');
	const [project, setProject] = useState<ProjectType>('custom');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFileInfo[]>([]);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
		{},
	);

	// 从项目获取基础路径
	const getBasePath = () => {
		const projectOption = PROJECT_OPTIONS.find((p) => p.value === project);
		return projectOption?.basePath || '/';
	};

	// 替换 URL 域名
	const replaceDomain = (url: string) => {
		try {
			const urlObj = new URL(url);
			urlObj.hostname =
				env === 'production' ? 'cdn.8848top.com' : 'cdn-dev.8848top.com';
			return urlObj.toString();
		} catch {
			return url;
		}
	};

	// 处理上传
	const handleUpload = async () => {
		if (fileList.length === 0) {
			message.warning('请先选择要上传的文件');
			return;
		}

		// 上传前更新配置
		beLinkCOS.updateConfig({ mode: env, basePath: getBasePath() });

		setUploading(true);

		const newUploadedFiles: UploadedFileInfo[] = [];

		try {
			for (const file of fileList) {
				const originFile = file.originFileObj;
				if (!originFile) continue;

				// 更新文件状态为上传中
				setFileList((prev) =>
					prev.map((f) =>
						f.uid === file.uid ? { ...f, status: 'uploading' } : f,
					),
				);

				try {
					const isImage = isImageFile(originFile);
					// 先从本地文件读取宽高（比上传后请求 url 更稳定）
					let dimensions: { width: number; height: number } | null = null;
					if (isImage) {
						try {
							dimensions = await getImageDimensionsFromFile(originFile);
						} catch (err) {
							// 忽略尺寸读取失败，仍返回 url 以保证上传功能可用
							console.warn('get image dimensions failed:', err);
						}
					}

					const result: UploadResult = await beLinkCOS.uploadFile(originFile, {
						onProgressCallback: (progressData) => {
							const percent = Math.round(progressData.percent * 100);
							setUploadProgress((prev) => ({ ...prev, [file.uid]: percent }));
						},
					});

					// 替换域名为 CDN 域名
					const cdnUrl = replaceDomain(result.url);

					newUploadedFiles.push({
						uid: file.uid,
						name: originFile.name,
						url: cdnUrl,
						isImage,
						width: dimensions?.width,
						height: dimensions?.height,
					});

					// 更新文件状态为完成
					setFileList((prev) =>
						prev.map((f) =>
							f.uid === file.uid ? { ...f, status: 'done' } : f,
						),
					);

					message.success(`${originFile.name} 上传成功`);
				} catch (error) {
					// 更新文件状态为错误
					setFileList((prev) =>
						prev.map((f) =>
							f.uid === file.uid ? { ...f, status: 'error' } : f,
						),
					);
					message.error(`${originFile.name} 上传失败`);
					console.error('Upload error:', error);
				}
			}

			setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
			// 上传成功后清空文件列表
			setFileList([]);
			setUploadProgress({});
		} finally {
			setUploading(false);
		}
	};

	// 复制链接到剪贴板
	const handleCopyUrl = async (url: string) => {
		try {
			await navigator.clipboard.writeText(url);
			message.success('链接已复制到剪贴板');
		} catch {
			message.error('复制失败，请手动复制');
		}
	};

	// 从列表中移除已上传的文件
	const handleRemoveUploaded = (uid: string) => {
		setUploadedFiles((prev) => prev.filter((f) => f.uid !== uid));
	};

	// 上传组件属性
	const uploadProps: UploadProps = {
		multiple: true,
		fileList,
		beforeUpload: (file) => {
			setFileList((prev) => [
				...prev,
				{
					uid: file.uid,
					name: file.name,
					status: 'done',
					originFileObj: file,
				} as UploadFile,
			]);
			return false; // 阻止自动上传
		},
		onRemove: (file) => {
			setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
		},
	};

	// 组件挂载时初始化 COS（仅执行一次）
	useEffect(() => {
		beLinkCOS.init({ mode: env });
	}, [env]); // 仅在挂载时运行一次

	return {
		// 状态
		env,
		project,
		fileList,
		uploadedFiles,
		uploading,
		uploadProgress,
		// 操作方法
		setEnv,
		setProject,
		getBasePath,
		handleUpload,
		handleCopyUrl,
		handleRemoveUploaded,
		// 组件属性
		uploadProps,
	};
}
